import { stringSimilarity } from 'string-similarity-js';

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

type SpanLoc = { spanIndex: number; charIndex: number };

const PDF_LAYER_SELECTOR = '.react-pdf__Page__textContent, .textLayer';
const PDF_CONTAINER_SELECTOR =
  '.react-pdf__Page, .react-pdf__Page__canvas, .react-pdf__Page__textContent, .textLayer';

function clearOldMarks(root: HTMLElement) {
  root.querySelectorAll('mark.__find_mark').forEach((m) => {
    const parent = m.parentNode!;
    parent.replaceChild(document.createTextNode(m.textContent ?? ''), m);
    parent.normalize();
  });
  root.querySelectorAll('.__find_overlay').forEach((el) => el.remove());
  root
    .querySelectorAll(
      '.react-pdf__Page__textContent span.__find_hit, .textLayer span.__find_hit'
    )
    .forEach((el) => el.classList.remove('__find_hit'));
}

function applySpanHighlights(
  spans: HTMLSpanElement[],
  spanToRanges: Map<number, Array<{ s: number; e: number }>>
) {
  const hitIndices = new Set<number>(spanToRanges.keys());
  for (let i = 0; i < spans.length; i++) {
    const sp = spans[i];
    if (hitIndices.has(i)) sp.classList.add('__find_hit');
    else sp.classList.remove('__find_hit');
  }
}

function needsBoundarySpace(prevText: string, nextText: string) {
  const a = prevText.slice(-1);
  const b = nextText.slice(0, 1);
  if (!a || !b) return false;
  if (/\s/.test(a) || /\s/.test(b)) return false;
  return true;
}

function normalizeChar(ch: string) {
  return /\s/.test(ch) ? ' ' : ch;
}

function buildNormalizedTextAndMap(spans: HTMLSpanElement[]) {
  let norm = '';
  const map: SpanLoc[] = [];

  for (let s = 0; s < spans.length; s++) {
    const raw = spans[s].textContent ?? '';

    if (s > 0) {
      const prev = spans[s - 1].textContent ?? '';
      if (needsBoundarySpace(prev, raw) && norm[norm.length - 1] !== ' ') {
        norm += ' ';
        map.push({ spanIndex: s, charIndex: 0 });
      }
    }

    let lastWasSpace = norm.length ? norm[norm.length - 1] === ' ' : false;

    for (let i = 0; i < raw.length; i++) {
      const out = normalizeChar(raw[i]);
      if (out === ' ') {
        if (lastWasSpace) continue;
        norm += ' ';
        map.push({ spanIndex: s, charIndex: i });
        lastWasSpace = true;
      } else {
        norm += out;
        map.push({ spanIndex: s, charIndex: i });
        lastWasSpace = false;
      }
    }
  }
  console.log('map ', map);
  return { norm, map };
}

function buildSearchTextAndMap(norm: string, map: SpanLoc[]) {
  let search = '';
  const searchMap: SpanLoc[] = [];

  for (let i = 0; i < norm.length; i++) {
    const ch = norm[i];
    if (ch === ' ') continue;
    if (
      ch === '-' &&
      i > 0 &&
      i < norm.length - 1 &&
      /[a-z0-9]/i.test(norm[i - 1]) &&
      /[a-z0-9]/i.test(norm[i + 1])
    ) {
      continue;
    }
    search += ch;
    searchMap.push(map[i]);
  }

  return { search, searchMap };
}

function normalizeQueryForSearch(q: string) {
  return q.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
}

function findAllOccurrences(haystack: string, needle: string) {
  const res: Array<{ start: number; end: number }> = [];
  if (!needle) return res;
  let idx = 0;
  while (true) {
    const at = haystack.indexOf(needle, idx);
    if (at === -1) break;
    res.push({ start: at, end: at + needle.length });
    idx = at + Math.max(1, needle.length);
  }
  return res;
}

function toSegments(map: SpanLoc[], start: number, end: number) {
  const segs = new Map<number, Array<{ s: number; e: number }>>();

  let prev: SpanLoc | null = null;
  let cur: { spanIndex: number; s: number; e: number } | null = null;

  for (let i = start; i < end; i++) {
    const loc = map[i];
    if (!loc) continue;

    if (!cur || loc.spanIndex !== cur.spanIndex) {
      if (cur) {
        const arr = segs.get(cur.spanIndex) ?? [];
        arr.push({ s: cur.s, e: cur.e });
        segs.set(cur.spanIndex, arr);
      }
      cur = {
        spanIndex: loc.spanIndex,
        s: loc.charIndex,
        e: loc.charIndex + 1
      };
    } else {
      const expected = prev ? prev.charIndex + 1 : loc.charIndex;
      if (
        loc.charIndex >= cur.e ||
        loc.charIndex === expected ||
        loc.charIndex === cur.e - 1
      ) {
        cur.e = Math.max(cur.e, loc.charIndex + 1);
      } else {
        const arr = segs.get(cur.spanIndex) ?? [];
        arr.push({ s: cur.s, e: cur.e });
        segs.set(cur.spanIndex, arr);
        cur = {
          spanIndex: loc.spanIndex,
          s: loc.charIndex,
          e: loc.charIndex + 1
        };
      }
    }

    prev = loc;
  }

  if (cur) {
    const arr = segs.get(cur.spanIndex) ?? [];
    arr.push({ s: cur.s, e: cur.e });
    segs.set(cur.spanIndex, arr);
  }

  for (const [k, arr] of segs.entries()) {
    arr.sort((a, b) => a.s - b.s);
    const merged: Array<{ s: number; e: number }> = [];
    for (const r of arr) {
      const last = merged[merged.length - 1];
      if (!last || r.s > last.e) merged.push({ ...r });
      else last.e = Math.max(last.e, r.e);
    }
    segs.set(k, merged);
  }

  return segs;
}

const cleanString = (str: string) => {
  return str.replace(/\r?\n/g, ' ');
};

type SpanMatch = { text: string; match: number; id: string };

/**
 * A span "matches" if its text is found anywhere inside matchString
 * (i.e., spanText is a substring of matchString).
 */
export function buildSpanMatches(
  spans: HTMLSpanElement[],
  matchString: string
): SpanMatch[] {
  return spans.map((span, i) => {
    const text = cleanString((span.textContent ?? '').trim());

    // Prefer an existing id; otherwise fall back to data-id; otherwise generate one
    const id = span.id || span.dataset.id || '';

    return {
      text,
      match: stringSimilarity(matchString, text),
      id
    };
  });
}

export function findAllMatching(root: HTMLElement, query: string) {
  const q = query.trim();
  if (!q) return;
  const allSpanMatches: any[] = [];
  const textLayers = Array.from(
    root.querySelectorAll(PDF_LAYER_SELECTOR)
  ) as HTMLElement[];
  textLayers.forEach((textLayer, index) => {
    console.log(`text layer ${index}`, textLayer);
    const spans = Array.from(
      textLayer.querySelectorAll('span.markedContent')
    ) as HTMLSpanElement[];
    console.log('spans', spans);
    const spanMatches = buildSpanMatches(spans, q);
    console.log('span matches', spanMatches);

    allSpanMatches.push(...spanMatches);
  });
  console.log('allSpanMatches', allSpanMatches);
  const sortedSPans = allSpanMatches.sort((a, b) => b.match - a.match);
  console.log('quesy string', q);
  console.log('sortedSPans', sortedSPans);
  if (sortedSPans[0].match > 0.6) {
    const el = document.getElementById(sortedSPans[0].id);
    console.log('applying class', el);
    if (el) {
      el.classList.add('__find_hit');
    }
  }
  return allSpanMatches;
}
function findAndMarkAcrossSpans(root: HTMLElement, query: string) {
  const q = query.trim();
  if (!q) return 0;

  const textLayers = Array.from(
    root.querySelectorAll(PDF_LAYER_SELECTOR)
  ) as HTMLElement[];
  if (!textLayers.length) return 0;

  let totalMatches = 0;
  let totalSpans = 0;

  for (const textLayer of textLayers) {
    console.log('text layer ------', textLayer);
    const spans = Array.from(
      textLayer.querySelectorAll('span')
    ) as HTMLSpanElement[];
    const spanMatches = buildSpanMatches(spans, q);
    console.log('span matches', spanMatches);
    if (!spans.length) continue;
    totalSpans += spans.length;

    const { norm, map } = buildNormalizedTextAndMap(spans);
    const { search, searchMap } = buildSearchTextAndMap(norm, map);
    const normHay = search.toLowerCase();
    const normNeedle = normalizeQueryForSearch(q);

    const matches = findAllOccurrences(normHay, normNeedle);
    if (!matches.length) continue;

    totalMatches += matches.length;

    const spanToRanges = new Map<number, Array<{ s: number; e: number }>>();
    for (const m of matches) {
      const segs = toSegments(searchMap, m.start, m.end);
      for (const [spanIndex, ranges] of segs.entries()) {
        const arr = spanToRanges.get(spanIndex) ?? [];
        arr.push(...ranges);
        spanToRanges.set(spanIndex, arr);
      }
    }

    applySpanHighlights(spans, spanToRanges);
  }

  console.log('[findAndHighlight] layers:', textLayers.length);
  console.log('[findAndHighlight] spans:', totalSpans);
  console.log('[findAndHighlight] matches:', totalMatches);

  return totalMatches;
}

export function findAndMarkInNode(root: HTMLElement, query: string) {
  clearOldMarks(root);

  const q = query.trim();
  if (!q) return 0;

  const isPdfContext = !!root.querySelector(PDF_CONTAINER_SELECTOR);
  if (isPdfContext) {
    const versionKey = '__findMarkVersion';
    const nextVersion = String(Number(root.dataset[versionKey] || 0) + 1);
    root.dataset[versionKey] = nextVersion;

    const run = () => {
      if (root.dataset[versionKey] !== nextVersion) return;
      const layers = Array.from(
        root.querySelectorAll(PDF_LAYER_SELECTOR)
      ) as HTMLElement[];
      if (!layers.length) {
        requestAnimationFrame(run);
        return;
      }
      const allReady = layers.every((layer) =>
        layer.querySelector('.endOfContent')
      );
      if (!allReady) {
        requestAnimationFrame(run);
        return;
      }
      findAndMarkAcrossSpans(root, q);
    };

    run();
    return 0;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue?.trim()) return NodeFilter.FILTER_REJECT;
      // ignore inside script/style
      const p = (node.parentElement?.tagName ?? '').toLowerCase();
      if (p === 'script' || p === 'style') return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    }
  });

  const re = new RegExp(escapeRegExp(q), 'gi');
  let count = 0;

  const textNodes: Text[] = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode as Text);

  for (const textNode of textNodes) {
    const text = textNode.nodeValue ?? '';
    re.lastIndex = 0;
    if (!re.test(text)) continue;

    const frag = document.createDocumentFragment();
    let last = 0;

    re.lastIndex = 0;
    for (let match; (match = re.exec(text)); ) {
      const start = match.index;
      const end = start + match[0].length;

      if (start > last)
        frag.appendChild(document.createTextNode(text.slice(last, start)));

      const mark = document.createElement('mark');
      mark.className = '__find_mark';
      mark.textContent = text.slice(start, end);
      frag.appendChild(mark);

      count++;
      last = end;
      if (match[0].length === 0) re.lastIndex++; // safety
    }

    if (last < text.length)
      frag.appendChild(document.createTextNode(text.slice(last)));

    textNode.parentNode?.replaceChild(frag, textNode);
  }

  return count;
}
